import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { VALUECALC } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtSGD } from "@/lib/format";
import {
  LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend, ReferenceLine,
} from "recharts";
import { Plus, Calculator, Trophy, Clock, Trash2, TrendingUp, Layers, Sliders, Settings2 } from "lucide-react";
import { toast } from "sonner";

const LINE_COLORS = ["#94a3b8", "#27523C", "#C86D51", "#2563eb"];
const ATTR_COLORS = ["#27523C", "#4E8765", "#C86D51", "#d97706", "#2563eb", "#7c3aed"];

// Sensible Singapore landscape-maintenance benefit benchmarks (SGD / year defaults, editable)
const BENEFIT_BENCHMARKS = [
  { category: "Water savings", amount: 1800 },
  { category: "Labour efficiency", amount: 1200 },
  { category: "Plant health", amount: 1000 },
  { category: "Compliance / penalties avoided", amount: 800 },
  { category: "Amenity & green cover", amount: 600 },
];

const BLANK = () => ({
  title: "", horizon_years: 5, discount_rate_pct: 5, benefit_variance_pct: 20, cost_variance_pct: 15,
  options: [
    { label: "Do nothing", capital_cost_sgd: 0, annual_cost_sgd: 0, benefit_breakdown: [] },
    { label: "Repair", capital_cost_sgd: 0, annual_cost_sgd: 0, benefit_breakdown: [] },
    { label: "Redesign", capital_cost_sgd: 0, annual_cost_sgd: 0, benefit_breakdown: [] },
  ],
});

export default function ValueCalcWorkspace() {
  const { hasRole } = useAuth();
  const ws = useWorkspace();
  const siteId = ws.siteId;
  const canEdit = hasRole("tenant_admin", "contract_manager", "finance_reviewer");
  const canAdmin = hasRole("tenant_admin");
  const [calcs, setCalcs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK());
  const [benchmarks, setBenchmarks] = useState(BENEFIT_BENCHMARKS);
  const [libOpen, setLibOpen] = useState(false);
  const [libRows, setLibRows] = useState([]);
  const [presets, setPresets] = useState([]);

  const load = useCallback(async () => {
    const r = await api.get("/value-calcs");
    setCalcs(r.data.filter((c) => !c.site_id || c.site_id === siteId));
  }, [siteId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/settings/benefit-benchmarks")
      .then((r) => { if (Array.isArray(r.data) && r.data.length) setBenchmarks(r.data); })
      .catch(() => {});
    api.get("/settings/benefit-benchmark-presets")
      .then((r) => { if (Array.isArray(r.data)) setPresets(r.data); })
      .catch(() => {});
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setOpt = (i, k, v) => setForm((f) => { const o = [...f.options]; o[i] = { ...o[i], [k]: v }; return { ...f, options: o }; });
  const setBreak = (oi, bi, k, v) => setForm((f) => {
    const o = [...f.options]; const bb = [...(o[oi].benefit_breakdown || [])];
    bb[bi] = { ...bb[bi], [k]: v }; o[oi] = { ...o[oi], benefit_breakdown: bb }; return { ...f, options: o };
  });
  const addBreak = (oi) => setForm((f) => { const o = [...f.options]; o[oi] = { ...o[oi], benefit_breakdown: [...(o[oi].benefit_breakdown || []), { category: "", amount: 0 }] }; return { ...f, options: o }; });
  const rmBreak = (oi, bi) => setForm((f) => { const o = [...f.options]; o[oi] = { ...o[oi], benefit_breakdown: o[oi].benefit_breakdown.filter((_, j) => j !== bi) }; return { ...f, options: o }; });
  const addBenchmark = (oi, b) => setForm((f) => {
    const o = [...f.options]; const bb = [...(o[oi].benefit_breakdown || [])];
    const idx = bb.findIndex((x) => x.category === b.category);
    if (idx === -1) bb.push({ category: b.category, amount: b.amount });
    else if (!Number(bb[idx].amount)) bb[idx] = { ...bb[idx], amount: b.amount };
    o[oi] = { ...o[oi], benefit_breakdown: bb }; return { ...f, options: o };
  });
  const loadDefaults = (oi) => setForm((f) => {
    const o = [...f.options]; const bb = [...(o[oi].benefit_breakdown || [])];
    benchmarks.forEach((b) => {
      const idx = bb.findIndex((x) => x.category === b.category);
      if (idx === -1) bb.push({ category: b.category, amount: b.amount });
      else if (!Number(bb[idx].amount)) bb[idx] = { ...bb[idx], amount: b.amount };
    });
    o[oi] = { ...o[oi], benefit_breakdown: bb }; return { ...f, options: o };
  });

  const save = async () => {
    try {
      const payload = {
        ...form, site_id: siteId,
        horizon_years: Number(form.horizon_years) || 5,
        discount_rate_pct: Number(form.discount_rate_pct) || 0,
        benefit_variance_pct: Number(form.benefit_variance_pct) || 0,
        cost_variance_pct: Number(form.cost_variance_pct) || 0,
        options: form.options.map((o) => ({
          label: o.label, capital_cost_sgd: Number(o.capital_cost_sgd) || 0,
          annual_cost_sgd: Number(o.annual_cost_sgd) || 0, annual_benefit_sgd: 0,
          benefit_breakdown: (o.benefit_breakdown || []).filter((b) => b.category).map((b) => ({ category: b.category, amount: Number(b.amount) || 0 })),
        })),
      };
      await api.post("/value-calcs", payload);
      toast.success("Comparison computed"); setOpen(false); setForm(BLANK()); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openLib = () => { setLibRows(benchmarks.map((b) => ({ ...b }))); setLibOpen(true); };
  const saveLib = async () => {
    try {
      const items = libRows.filter((r) => r.category).map((r) => ({ category: r.category, amount: Number(r.amount) || 0 }));
      const { data } = await api.put("/settings/benefit-benchmarks", { items });
      setBenchmarks(data); setLibOpen(false);
      toast.success("Benchmark library saved");
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid={VALUECALC.root}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">Multi-year NPV, payback, sensitivity and benefit attribution for do-nothing / repair / redesign</p>
        <div className="flex shrink-0 gap-2">
          {canAdmin && <Button size="sm" variant="outline" onClick={openLib} data-testid={VALUECALC.benchLibBtn}><Settings2 className="mr-1 h-4 w-4" /> Benchmark library</Button>}
          {canEdit && <Button size="sm" onClick={() => { setForm(BLANK()); setOpen(true); }} data-testid={VALUECALC.addBtn} className="bg-forest-mid hover:bg-forest-dark"><Plus className="mr-1 h-4 w-4" /> New comparison</Button>}
        </div>
      </div>

      <div className="space-y-5">
        {calcs.length === 0 && <p className="text-sm text-muted-foreground">No comparisons yet.</p>}
        {calcs.map((c) => {
          const rec = (c.results || []).find((r) => r.label === c.recommended);
          const years = c.horizon_years || 5;
          // merge cashflow series into one chart dataset {year, [label]: cumulative}
          const chart = [];
          for (let y = 0; y <= years; y++) {
            const row = { year: `Y${y}` };
            (c.results || []).forEach((r) => {
              const pt = (r.cashflow || []).find((p) => p.year === y);
              row[r.label] = pt ? pt.cumulative : null;
            });
            chart.push(row);
          }
          const attr = (rec?.benefit_breakdown || []);
          const attrTotal = attr.reduce((s, a) => s + (a.amount || 0), 0) || 1;
          return (
            <Card key={c.id} className="border-sage-light p-5" data-testid={VALUECALC.card(c.id)}>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-forest-dark"><Calculator className="h-4 w-4 text-forest-mid" /> {c.title}</h3>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Sliders className="h-3.5 w-3.5" /> {c.discount_rate_pct ?? 5}% discount · ±{c.benefit_variance_pct ?? 20}% benefit</span>
                  {c.recommended && <span className="flex items-center gap-1 rounded-full bg-forest-mid px-3 py-1 font-semibold text-white"><Trophy className="h-3.5 w-3.5" /> {c.recommended}</span>}
                </div>
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Option</TableHead><TableHead>Capital</TableHead>
                      <TableHead>NPV ({years}y)</TableHead><TableHead>Payback</TableHead>
                      <TableHead>Sensitivity range</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(c.results || []).map((r) => {
                      const s = r.sensitivity || {};
                      const span = Math.max(1, (s.high || 0) - (s.low || 0));
                      return (
                        <TableRow key={r.label} className={r.label === c.recommended ? "bg-sage-surface" : ""}>
                          <TableCell className="text-sm font-medium">{r.label}</TableCell>
                          <TableCell className="text-sm">{fmtSGD(r.capital_cost_sgd)}</TableCell>
                          <TableCell className={`text-sm font-semibold ${r.npv_sgd >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{fmtSGD(r.npv_sgd)}</TableCell>
                          <TableCell className="text-sm">{r.payback_years != null ? <span className="flex items-center gap-1"><Clock className="h-3 w-3 text-muted-foreground" />{r.payback_years}y</span> : "—"}</TableCell>
                          <TableCell className="min-w-[180px]">
                            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                              <span>{fmtSGD(s.low)}</span>
                              <div className="relative h-1.5 flex-1 rounded-full bg-slate-100">
                                <div className="absolute h-1.5 rounded-full bg-terracotta/60" style={{ left: 0, width: "100%" }} />
                                <div className="absolute top-1/2 h-3 w-0.5 -translate-y-1/2 bg-forest-mid" style={{ left: `${Math.min(100, Math.max(0, (((s.base || 0) - (s.low || 0)) / span) * 100))}%` }} />
                              </div>
                              <span>{fmtSGD(s.high)}</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="lg:col-span-2">
                  <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><TrendingUp className="h-3.5 w-3.5" /> Cumulative discounted cashflow</p>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={chart} margin={{ top: 6, right: 8, left: -12, bottom: 0 }}>
                      <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v) => fmtSGD(v)} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <ReferenceLine y={0} stroke="#cbd5e1" />
                      {(c.results || []).map((r, i) => (
                        <Line key={r.label} type="monotone" dataKey={r.label} stroke={LINE_COLORS[i % LINE_COLORS.length]} strokeWidth={2} dot={false} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Layers className="h-3.5 w-3.5" /> Benefit attribution · {c.recommended}</p>
                  {attr.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No breakdown captured for the recommended option.</p>
                  ) : (
                    <div className="space-y-2">
                      {attr.map((a, i) => (
                        <div key={i}>
                          <div className="flex justify-between text-xs">
                            <span className="text-forest-dark">{a.category}</span>
                            <span className="font-mono text-muted-foreground">{fmtSGD(a.amount)}/yr</span>
                          </div>
                          <div className="mt-0.5 h-2 rounded-full bg-slate-100">
                            <div className="h-2 rounded-full" style={{ width: `${(a.amount / attrTotal) * 100}%`, backgroundColor: ATTR_COLORS[i % ATTR_COLORS.length] }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">New value comparison</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="col-span-2 space-y-1.5"><Label>Title</Label><Input value={form.title} onChange={(e) => setField("title", e.target.value)} data-testid={VALUECALC.field("title")} /></div>
              <div className="space-y-1.5"><Label>Horizon (yrs)</Label><Input type="number" value={form.horizon_years} onChange={(e) => setField("horizon_years", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Discount %</Label><Input type="number" value={form.discount_rate_pct} onChange={(e) => setField("discount_rate_pct", e.target.value)} data-testid={VALUECALC.field("discount")} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Benefit variance ± %</Label><Input type="number" value={form.benefit_variance_pct} onChange={(e) => setField("benefit_variance_pct", e.target.value)} data-testid={VALUECALC.field("benefit-var")} /></div>
              <div className="space-y-1.5"><Label>Cost variance ± %</Label><Input type="number" value={form.cost_variance_pct} onChange={(e) => setField("cost_variance_pct", e.target.value)} /></div>
            </div>

            <div className="space-y-3">
              {form.options.map((o, i) => (
                <Card key={i} className="border-sage-light p-3">
                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-1"><Label className="text-xs">Option</Label><Input className="h-9" value={o.label} onChange={(e) => setOpt(i, "label", e.target.value)} data-testid={VALUECALC.field(`opt-label-${i}`)} /></div>
                    <div className="space-y-1"><Label className="text-xs">Capital (SGD)</Label><Input className="h-9" type="number" value={o.capital_cost_sgd} onChange={(e) => setOpt(i, "capital_cost_sgd", e.target.value)} data-testid={VALUECALC.field(`opt-capital-${i}`)} /></div>
                    <div className="space-y-1"><Label className="text-xs">Annual cost</Label><Input className="h-9" type="number" value={o.annual_cost_sgd} onChange={(e) => setOpt(i, "annual_cost_sgd", e.target.value)} /></div>
                  </div>
                  <div className="mt-2">
                    <div className="mb-1 flex items-center justify-between">
                      <Label className="text-xs text-muted-foreground">Annual benefit attribution</Label>
                      <Button type="button" size="sm" variant="ghost" className="h-6 text-xs" onClick={() => addBreak(i)}><Plus className="mr-1 h-3 w-3" /> benefit</Button>
                    </div>
                    <div className="mb-1.5 flex flex-wrap gap-1">
                      {benchmarks.map((b) => (
                        <button type="button" key={b.category} onClick={() => addBenchmark(i, b)}
                          className="rounded-full border border-sage-light bg-sage-surface px-2 py-0.5 text-[10px] font-medium text-forest-dark transition-colors hover:bg-sage-light"
                          data-testid={VALUECALC.field(`bench-${i}-${b.category.split(" ")[0].toLowerCase()}`)}>
                          + {b.category}
                        </button>
                      ))}
                      <button type="button" onClick={() => loadDefaults(i)}
                        className="rounded-full border border-forest-mid/40 bg-white px-2 py-0.5 text-[10px] font-semibold text-forest-mid hover:bg-sage-surface"
                        data-testid={VALUECALC.field(`bench-sg-${i}`)}>
                        ★ SG defaults
                      </button>
                    </div>
                    {(o.benefit_breakdown || []).length === 0 && <p className="text-[11px] text-muted-foreground">No benefit lines (counts as zero benefit).</p>}
                    {(o.benefit_breakdown || []).map((b, bi) => (
                      <div key={bi} className="mb-1 grid grid-cols-[1fr,110px,auto] gap-2">
                        <Input className="h-8 text-sm" placeholder="e.g. Water savings" value={b.category} onChange={(e) => setBreak(i, bi, "category", e.target.value)} data-testid={VALUECALC.field(`opt-${i}-cat-${bi}`)} />
                        <Input className="h-8 text-sm" type="number" placeholder="SGD/yr" value={b.amount} onChange={(e) => setBreak(i, bi, "amount", e.target.value)} data-testid={VALUECALC.field(`opt-${i}-amt-${bi}`)} />
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-rose-500" onClick={() => rmBreak(i, bi)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">NPV discounts incremental cashflows vs the "Do nothing" baseline; sensitivity flexes benefits and costs by the variances above.</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="bg-forest-mid hover:bg-forest-dark" data-testid={VALUECALC.saveBtn}>Compute & save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={libOpen} onOpenChange={setLibOpen}>
        <DialogContent className="max-w-md" data-testid={VALUECALC.benchLibDialog}>
          <DialogHeader><DialogTitle className="font-heading">Benefit benchmark library</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Set your organisation's default annual benefit values (SGD). Every new value comparison starts from these.</p>
          {presets.length > 0 && (
            <div className="rounded-lg border border-sage-light bg-sage-surface p-2.5">
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Load a ready-made set, then customise</p>
              <div className="flex flex-wrap gap-1.5">
                {presets.map((p, i) => (
                  <button key={i} type="button" onClick={() => { setLibRows(p.items.map((x) => ({ ...x }))); toast.success(`Loaded "${p.name}" — edit and save`); }}
                    className="rounded-full border border-forest-mid/40 bg-white px-2.5 py-1 text-[11px] font-medium text-forest-dark transition-colors hover:bg-sage-light"
                    data-testid={VALUECALC.presetBtn(i)}>
                    {p.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-2">
            {libRows.map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr,110px,auto] gap-2">
                <Input className="h-9" value={r.category} onChange={(e) => setLibRows((rows) => rows.map((x, j) => (j === i ? { ...x, category: e.target.value } : x)))} placeholder="Category" />
                <Input className="h-9" type="number" value={r.amount} onChange={(e) => setLibRows((rows) => rows.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))} placeholder="SGD/yr" />
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-rose-500" onClick={() => setLibRows((rows) => rows.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" onClick={() => setLibRows((rows) => [...rows, { category: "", amount: 0 }])}><Plus className="mr-1 h-3.5 w-3.5" /> Add benchmark</Button>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setLibOpen(false)}>Cancel</Button><Button onClick={saveLib} className="bg-forest-mid hover:bg-forest-dark" data-testid={VALUECALC.benchLibSave}>Save library</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
